import { classes, icons } from '@portal/components'
import React, { useMemo } from 'react'

import { findCollectionName } from '../../utils'
import { DocumentDetailDrawer } from '../documentDetail'
import { QueryEditor } from '../queryEditor'
import { QueryResultTable } from '../queryResultTable'
import { SideBar } from '../Sidebar'
import CollectionsList from '../Sidebar/CollectionsList'
import QueryHistoryList from '../Sidebar/QueryHistoryList'
import { TopBar } from '../topBar'
import { InitialPreviewOverlay } from '../tours'
import { useDQLEditorContext } from './context'

const { SideBarClose } = icons

export default function DQLEditorLayout() {
  const {
    query,
    sideBarOpen,
    setSideBarOpen,
    historySideBarOpen,
    setHistorySideBarOpen,
    activeView,
    activeRow,
    result,
  } = useDQLEditorContext()

  const activeCollectionName = useMemo(() => {
    return findCollectionName(query || '')
  }, [query])

  const CollectionsSideBar = () => (
    <SideBar
      title="Collections"
      side="left"
      open={sideBarOpen}
      closeHandler={() => setSideBarOpen(false)}
      closeIcon={<SideBarClose className="size-4" aria-hidden="true" />}
    >
      <CollectionsList />
    </SideBar>
  )

  const resultsTableTitle = useMemo(() => {
    if (activeCollectionName) {
      return `Documents for ${activeCollectionName}`
    }

    if (!!result) {
      return result.queryType === 'mutation' ? 'Mutated documents' : 'Documents'
    }

    return ''
  }, [activeCollectionName, result])

  if (activeView === 'collections') {
    return (
      <>
        <div className="fixed inset-0 flex h-screen w-screen flex-col">
          <TopBar />

          <div className="relative flex h-full w-full">
            <CollectionsSideBar />

            <div
              className={classes(
                'relative flex h-full w-full flex-col transition-[margin] duration-200',
                {
                  'mr-56 md:mr-[500px]': !!activeRow,
                  'ml-48 md:ml-80': sideBarOpen,
                },
              )}
              data-testid="dqlEditorAndResults"
            >
              <QueryResultTable
                title={resultsTableTitle}
                showResults={false}
                allowDelete={false}
                action="import"
                canSelectRows={false}
                sideBarOpen={sideBarOpen}
              />
            </div>

            {activeRow && <DocumentDetailDrawer />}
          </div>
        </div>

        <InitialPreviewOverlay />
      </>
    )
  }

  return (
    <>
      <div className="fixed inset-0 flex h-screen w-screen flex-col">
        <TopBar />

        <div className="relative flex h-full w-full">
          <CollectionsSideBar />

          <div
            className={classes(
              'relative flex h-full w-full flex-col transition-[margin] duration-200',
              {
                'mr-56 md:mr-[500px]': !!activeRow,
                'ml-48 md:ml-80': sideBarOpen,
                'mr-48 md:mr-80': historySideBarOpen && !activeRow,
              },
            )}
            data-testid="dqlEditorAndResults"
          >
            <QueryEditor />
            <QueryResultTable sideBarOpen={true} />
          </div>

          {activeRow && <DocumentDetailDrawer />}
          {!activeRow && activeView === 'editor' && (
            <SideBar
              title="Queries"
              side="right"
              open={historySideBarOpen}
              closeHandler={() => setHistorySideBarOpen(false)}
            >
              <QueryHistoryList />
            </SideBar>
          )}
        </div>
      </div>
    </>
  )
}
